import {srcSetBelow} from "../../../common/js/srcset";

/**
 * A large image of the lightbox which does not load falls back to the next smaller file of
 * its srcset before the lightbox says that it cannot be loaded (concepts/frontend-design.md,
 * "The lightbox"). Flickr's CDN refuses the sizes from 1600 px (_h, _k) to some visitors with
 * HTTP 429, and a phone with a dense screen picks _h for the lightbox: until 7.0.0 it showed
 * the error text where _b (1024 px) would have loaded. A script cannot see the status of an
 * image; it sees that the file did not load, which is the same for a 429, a 404 or a CDN
 * which is down, and the smaller file is the better answer to all of them.
 *
 * PhotoSwipe loads an image in Content.loadImage(); the event contentLoadImage lets the
 * script take that over. The takeover does what PhotoSwipe does - the sizes, the srcset, the
 * src, the alt, the load and the error - and on an error tries the candidates below the one
 * which failed; the smaller srcset stays in the data of the slide, so the image is not asked
 * for again when the visitor comes back to it. A slide falls back once (egSizeFallback in its
 * data, as the branch of #1916 had it): when the smaller file does not load either, or
 * nothing smaller is left, the lightbox shows its error text. So an image which is really
 * broken costs two requests and not one per size of the ladder - each of them a PHP request
 * for a local image on a shared host.
 *
 * @param {import('photoswipe').default} pswp the lightbox, before its init()
 */
export function bindImageFallback(pswp) {
    pswp.on('contentLoadImage', (event) => {
        const content = event.content;
        const image = content.element;
        if (!content.isImageContent() || !(image instanceof HTMLImageElement)) {
            return;
        }
        event.preventDefault();
        loadWithFallback(content, image);
    });
}

/**
 * @param content PhotoSwipe's Content of the slide
 * @param {HTMLImageElement} image its element
 */
export function loadWithFallback(content, image) {
    const data = content.data;
    content.updateSrcsetSizes();
    if (data.srcset) {
        image.srcset = data.srcset;
    }
    image.src = data.src ?? '';
    image.alt = data.alt ?? '';
    content.state = 'loading';

    image.onload = () => content.onLoaded();
    image.onerror = () => {
        const failed = image.currentSrc || image.src;
        // once per slide: a slide which fell back already shows the error
        const smaller = data.egSizeFallback ? null : srcSetBelow(data.srcset || '', failed, Number(data.width) || 0);
        if (!smaller || smaller.srcset === data.srcset) {
            content.onError();
            return;
        }
        data.egSizeFallback = true;
        data.srcset = smaller.srcset;
        data.src = smaller.src;
        image.srcset = smaller.srcset;
        image.src = smaller.src;
    };

    // a file from the cache of the browser: loaded at once, as PhotoSwipe counts it
    if (image.complete && image.naturalWidth > 0) {
        image.onload = null;
        content.onLoaded();
    }
}
